// React
import { memo, useCallback } from "react";

// Servisni sloj
import { initialPriceTier } from "services/api/pricing/consts";
import { PricingTierFormValues } from "services/api/pricing/types";

// UI
import { Button } from "ui/components/Button/Button";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";
import PricingTierRow from "./components/PricingTierRow/PricingTierRow";

// Tipovi
import { PricingTiersStepProps } from "./types";

/**
 * Cjenovni razredi po broju vozila.
 *
 * Stopa je udio pune cijene, ne popust: 100 % znači punu cijenu. Spojnost razreda provjerava
 * backend, rupa između njih se ne sprema.
 */
const PricingTiersStep: React.FC<PricingTiersStepProps> = (props: PricingTiersStepProps) => {
  const { formValues, setFormValues, errors } = props;

  const handleFieldChange = useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;

      const [, field, rawIndex] = name.split("|");
      const index = Number(rawIndex);

      setFormValues((previous) => ({
        ...previous,
        priceTiers: previous.priceTiers.map((tier, i) =>
          i === index ? { ...tier, [field]: value } : tier
        ),
      }));
    },
    [setFormValues]
  );

  const handleAddTier = useCallback(() => {
    setFormValues((previous) => ({
      ...previous,
      priceTiers: [
        ...previous.priceTiers,
        { ...initialPriceTier } as PricingTierFormValues,
      ],
    }));
  }, [setFormValues]);

  const handleRemoveTier = useCallback(
    (index: number) => {
      setFormValues((previous) => ({
        ...previous,
        priceTiers: previous.priceTiers.filter((_, i) => i !== index),
      }));
    },
    [setFormValues]
  );

  return (
    <>
      <PricingStepIntro
        title="Cjenovni razredi po broju vozila"
        description={
          <>
            Postavite razrede po broju vozila i udio pune cijene za svaki. Stopa je{" "}
            <strong>udio pune cijene, ne popust</strong>. 100 % znači punu cijenu, a prazan „do"
            otvoren razred prema beskonačnosti.
          </>
        }
      />

      <div className="p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col gap-3">
          {formValues.priceTiers.map((tier, index) => (
            <PricingTierRow
              key={index}
              index={index}
              tier={tier}
              errors={errors}
              onFieldChange={handleFieldChange}
              onRemove={handleRemoveTier}
            />
          ))}
        </div>

        {/* Greška cijele ljestvice ide ispod redova, uz njih na koje se odnosi. */}
        {errors.priceTiers && (
          <p className="text-sm text-red-600 mt-2">{errors.priceTiers}</p>
        )}

        <div className="mt-4">
          <Button variant="outlined" children="Dodaj razred" onClick={handleAddTier} />
        </div>
      </div>
    </>
  );
};

export default memo(PricingTiersStep);
